import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from './Assistant/ordersActions';
import { MyOrder, TicketMo } from 'ts/shared/interfaces/MOResponse';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import { getTicket, updateTicket } from 'services/myOrders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import { showSuccess, showWarning } from 'utils/utils';
import { dateFormat } from 'utils/shared/utils';
interface MoTicketProps {
  myOrder: MyOrder;
}

const MoTicket: FC<MoTicketProps> = ({ myOrder }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const [isLoading, setIsLoading] = useState(false);
  const [ticket, setTicket] = useState<TicketMo>();
  const authors = useRef<{ user: string; userId: number }[]>([]);
  const [message, setMessage] = useState('');
  const pendingReply = useRef('');
  const isActive =
    activeOrder?.orderRow.segmentId === myOrder.orderRow.segmentId ||
    activeOrder?.fullSegment.orderSegId === myOrder.fullSegment.orderSegId;
  useEffect(() => {
    setTicket(undefined);
    authors.current = [];

    (async () => {
      const ticket = await getTicket({
        orderId: myOrder.orderRow.orderId,
        ticketId: +myOrder.order.hp_Ticket,
        segmentId: myOrder.orderRow.segmentId,
      });

      authors.current = ticket?.comments.reduce((acc: any, comment) => {
        if (
          comment?.metadata?.flagsOptions?.the11?.message?.userId &&
          !acc.includes(comment.metadata.flagsOptions.the11.message.userId)
        ) {
          acc.push(comment.metadata.flagsOptions.the11.message);
        }
        return acc;
      }, []);

      setTicket(ticket);
    })();
  }, [myOrder]);

  const getStatusStyle = (status: string) => {
    let backgroundColor = '#F4A259';
    let color = '#000000';
    if (status === 'open') {
      backgroundColor = '#D9534F';
      color = '#FFFFFF';
    }
    if (status === 'pending') {
      backgroundColor = '#337AB7';
      color = '#FFFFFF';
    }
    if (status === 'solved') {
      backgroundColor = '#5A6268';
      color = '#3D454D';
    }

    return {
      color,
      backgroundColor,
      padding: '5px 10px',
      borderRadius: '5px',
    };
  };

  const sendReply = async (text: string) => {
    if (!text) {
      showWarning(t('mo.please_enter_message'));
      return;
    }
    if (!ticket) {
      pendingReply.current = text;
      return;
    }

    setMessage(text);
    setIsLoading(true);

    const response = await updateTicket({
      orderId: myOrder.orderRow.orderId,
      ticketId: +myOrder.order.hp_Ticket,
      segmentId: myOrder.orderRow.segmentId,
      requestMessage: text,
      subject: ticket?.subject,
    });

    if (!response.hasErrors) {
      const ticket = await getTicket({
        orderId: myOrder.orderRow.orderId,
        ticketId: +myOrder.order.hp_Ticket,
        segmentId: myOrder.orderRow.segmentId,
      });

      setMessage('');
      setTicket(ticket);

      showSuccess(t('mo.ticket_updated'));
    }

    setIsLoading(false);
  };

  const onClickSend = () => {
    void sendReply(message);
  };

  useEffect(() => {
    if (!ticket || !pendingReply.current) return;
    const text = pendingReply.current;
    pendingReply.current = '';
    void sendReply(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  useOrdersAction(
    (action) =>
      action.action === 'ticket-reply' &&
      actionTargetsOrder(
        action,
        { segmentId: myOrder.orderRow.segmentId, orderId: myOrder.orderRow.orderId },
        isActive,
      ),
    (action) => {
      if (action.message) void sendReply(action.message);
    },
    isActive,
  );

  return (
    <Fragment>
      {!!ticket && (
        <div className="mb-20 text-sm">
          <div className="table-default mb-20 mt-20">
            <table className="table-bordered-lines table-mobile">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('mo.subject')}</th>
                  <th>{t('mo.desc')}</th>
                  <th>{t('mo.status_full')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{ticket.id}</td>
                  <td>{ticket.subject}</td>
                  <td>{ticket.description}</td>
                  <td>
                    <span style={{ ...getStatusStyle(ticket.status), textTransform: 'capitalize' }}>
                      {ticket.status}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mb-10">
            {ticket.comments.map((comment, index) => (
              <div key={index} className="display-flex ml-10">
                <FontAwesomeIcon icon={faCircleUser} className="mr-10" size="xl" />
                <div className="flex-column">
                  <div>
                    <span className="mr-10 text-bold">
                      {comment?.metadata?.flagsOptions?.the11?.message?.user ||
                        authors.current.find((author) => author.userId === comment.authorId)
                          ?.user ||
                        'Comment'}
                    </span>
                    <span className="text-light">
                      {dateFormat(comment.createdAt, `MMM dd, yyyy - HH:mm:ss`)}
                    </span>
                  </div>
                  <div className="mo-message">{comment.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mb-10 mo-message-reply">
            <div className="mb-10">{t('mo.reply')}</div>
            <textarea
              rows={3}
              className="form-control form-control-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ height: '120px' }}
            ></textarea>
            <div className="mt-10 mb-20">
              <Button title={t('mo.send')} onClick={onClickSend} disabled={isLoading} />
            </div>
          </div>
        </div>
      )}
      {!ticket && (
        <div className="mb-20 text-sm">
          <div className="table-default mb-20 mt-20">
            <table className="table-bordered-lines table-mobile">
              <thead>
                <tr>
                  <th>
                    <Skeleton width={30} />
                  </th>
                  <th>
                    <Skeleton width={150} />
                  </th>
                  <th>
                    <Skeleton width={200} />
                  </th>
                  <th>
                    <Skeleton width={60} />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <Skeleton width={30} />
                  </td>
                  <td>
                    <Skeleton width={150} />
                  </td>
                  <td>
                    <Skeleton width={200} />
                  </td>
                  <td>
                    <Skeleton width={80} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            {[...Array(5)].map((_, index) => (
              <div key={index} className="display-flex ml-10 mb-20">
                <Skeleton circle width={30} height={30} />
                <div className="flex-column ml-10" style={{ width: '100%' }}>
                  <div className="mb-10">
                    <Skeleton width={120} height={16} />
                  </div>
                  <Skeleton height={40} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default MoTicket;
